import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatPhone, isoDay, nowMs } from '@dorixona/utils'
import { DetailHead } from '../../ui/DetailHead'
import { Icon } from '../../ui/Icon'
import { DatePicker } from '../../ui/DatePicker'
import { Select } from '../../ui/Select'
import { Loading, failureText } from '../../ui/Load'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { useSession } from '../auth/session'
import { useCustomer, useSaveCustomer, type Customer, type CustomerLang } from './api'
import { displayName, isDeleted } from './model'

export interface CustomerEditScreenProps {
  onToast: (message: string) => void
}

const LANGS: { value: CustomerLang; label: string }[] = [
  { value: 'ru', label: 'Русский' },
  { value: 'uz', label: 'Oʻzbekcha' },
  { value: 'en', label: 'English' },
]

/**
 * Правка профиля клиента — отдельной страницей (`/customers/:id/edit`).
 * Удалённого не правят: сервер ответит 404, поэтому формы нет.
 */
export function CustomerEditScreen({ onToast }: CustomerEditScreenProps) {
  const navigate = useNavigate()
  const params = useParams()
  const id = Number(params.id)
  const { can } = useSession()
  const card = useCustomer(id)

  if (card.isPending) return <Loading what="клиента" />
  const customer = card.data?.customer
  if (!customer || isDeleted(customer) || !can('customers', 'u')) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-users" />
          <b>{customer ? 'Править этого клиента нельзя' : 'Клиент не найден'}</b>
          {card.isError ? <p>{failureText(card.error)}</p> : null}
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate(customer ? `/customers/${customer.id}` : '/customers')
            }}
          >
            {customer ? 'К клиенту' : 'К списку клиентов'}
          </button>
        </div>
      </div>
    )
  }
  return <CustomerEdit key={customer.id} customer={customer} onToast={onToast} />
}

interface CustomerEditProps {
  customer: Customer
  onToast: (message: string) => void
}

/**
 * Правка профиля по обращению клиента (R-56): имя, пол, язык и дата рождения —
 * её клиент задаёт один раз сам, исправить потом может только сотрудник.
 * Телефон здесь не меняется: это логин и карта лояльности, его меняет сам
 * клиент кодом из СМС.
 */
function CustomerEdit({ customer, onToast }: CustomerEditProps) {
  const navigate = useNavigate()
  const save = useSaveCustomer()
  const [firstName, setFirstName] = useState(customer.firstName)
  const [lastName, setLastName] = useState(customer.lastName)
  // Пустое имя краснеет, когда его стёрли или ушли из пустого поля: у клиента,
  // который не назвался, форма не должна открываться в красном.
  const [left, setLeft] = useState<ReadonlySet<'first' | 'last'>>(new Set())
  const missing = (field: 'first' | 'last', value: string, initial: string) =>
    value.trim() === '' && (initial.trim() !== '' || left.has(field))
  const leave = (field: 'first' | 'last') => () => {
    if (!left.has(field)) setLeft(new Set(left).add(field))
  }
  const [gender, setGender] = useState<'male' | 'female'>(
    customer.gender === 'female' ? 'female' : 'male',
  )
  const [lang, setLang] = useState<CustomerLang>(customer.lang)
  const [birth, setBirth] = useState(customer.birth)
  const [error, setError] = useState('')
  const [dirty, setDirty] = useState(false)
  useUnsavedWarning(dirty)
  const ready = firstName.trim() !== '' && lastName.trim() !== ''
  /** Любая правка — несохранённое изменение: панель внизу это показывает. */
  const change =
    <T,>(set: (value: T) => void) =>
    (value: T) => {
      set(value)
      setDirty(true)
    }
  const back = () => {
    void navigate(`/customers/${customer.id}`)
  }

  function submit() {
    setError('')
    save.mutate(
      {
        id: customer.id,
        patch: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          gender,
          lang,
          birth,
        },
      },
      {
        onSuccess: () => {
          setDirty(false)
          onToast('Данные клиента сохранены')
          back()
        },
        onError: (failure) => {
          setError(failureText(failure))
        },
      },
    )
  }

  return (
    <>
      <DetailHead
        backLabel={displayName(customer)}
        onBack={back}
        title="Изменить данные клиента"
        sub={<span className="num">{formatPhone(customer.phone)}</span>}
      />

      <div className="ui-detail-grid">
        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="cus-profile">
            <div className="ui-card-head">
              <h3 id="cus-profile">Профиль</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-form-row">
                <div className="ui-field">
                  <label className="ui-label" htmlFor="cus-first">
                    Имя
                  </label>
                  <input
                    id="cus-first"
                    placeholder="Например, Азиз"
                    className="ui-input"
                    value={firstName}
                    autoComplete="off"
                    aria-invalid={
                      missing('first', firstName, customer.firstName) ? true : undefined
                    }
                    onBlur={leave('first')}
                    onChange={(e) => {
                      change(setFirstName)(e.target.value)
                    }}
                  />
                </div>
                <div className="ui-field">
                  <label className="ui-label" htmlFor="cus-last">
                    Фамилия
                  </label>
                  <input
                    id="cus-last"
                    placeholder="Например, Каримов"
                    className="ui-input"
                    value={lastName}
                    autoComplete="off"
                    aria-invalid={missing('last', lastName, customer.lastName) ? true : undefined}
                    onBlur={leave('last')}
                    onChange={(e) => {
                      change(setLastName)(e.target.value)
                    }}
                  />
                </div>
              </div>
              <div className="ui-form-row">
                <div className="ui-field">
                  <span className="ui-label" aria-hidden="true">
                    Пол
                  </span>
                  <Select
                    label="Пол"
                    value={gender}
                    options={[
                      { value: 'male', label: 'Мужской' },
                      { value: 'female', label: 'Женский' },
                    ]}
                    onChange={change(setGender)}
                  />
                </div>
                <div className="ui-field">
                  <span className="ui-label" aria-hidden="true">
                    Язык приложения
                  </span>
                  <Select
                    label="Язык приложения"
                    value={lang}
                    options={LANGS}
                    onChange={change(setLang)}
                  />
                </div>
              </div>
              <div className="ui-field">
                <label className="ui-label" htmlFor="cus-birth">
                  Дата рождения <span className="opt">необязательно</span>
                </label>
                <DatePicker
                  id="cus-birth"
                  label="Дата рождения"
                  value={birth}
                  max={isoDay(nowMs())}
                  onChange={change(setBirth)}
                />
              </div>
              {error ? (
                <div className="ui-error" role="alert">
                  <Icon name="i-alert" />
                  {error}
                </div>
              ) : null}
            </div>
          </section>
        </div>
        <div className="ui-detail-col">
          <section className="ui-card" aria-labelledby="cus-login">
            <div className="ui-card-head">
              <h3 id="cus-login">Вход</h3>
            </div>
            <div className="ui-card-body ui-form">
              <div className="ui-facts">
                <div>
                  <span className="k">Телефон</span>
                  <span className="v num">{formatPhone(customer.phone)}</span>
                </div>
              </div>
              <span className="ui-hint">Телефон меняет сам клиент кодом из СМС</span>
            </div>
          </section>
        </div>
      </div>

      <div className="ui-savebar" data-dirty={dirty || undefined}>
        <span className="state" role="status">
          {dirty && !ready
            ? 'Укажите имя и фамилию'
            : dirty
              ? 'Есть несохранённые изменения'
              : 'Все изменения сохранены'}
        </span>
        <button type="button" className="ui-btn" data-variant="secondary" onClick={back}>
          {dirty ? 'Отменить' : 'К клиенту'}
        </button>
        <button
          type="button"
          className="ui-btn"
          disabled={!dirty || !ready || save.isPending}
          aria-busy={save.isPending}
          onClick={submit}
        >
          Сохранить
        </button>
      </div>
    </>
  )
}
